import React, { useEffect, useState } from 'react';
import FileUploader from '../../file_uploader/FileUploader';
import { getToken } from '../utils/auth';
import { fetchOrderById, poUploadOrder } from '../api/orders';
import { pushDataLayerEvent, trackKlaviyoEvent } from '../../utils/utils';

function getOrderIdFromHash() {
  const hash = window.location.hash || '';
  const match = hash.match(/#\/order\/([^/?#]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

export function isGovernmentPoOrder(order) {
  const v = order?.governmentPo;
  if (v === true || v === 'true') return true;
  if (v && typeof v === 'object' && (v.value === true || v.value === 'true')) return true;
  return false;
}

export function hasPurchaseOrderFile(order) {
  return !!(order?.governmentPoDocument);
}

/**
 * Purchase order uploader for gov-PO orders that do not yet have a file.
 *
 * Uploads to POST /api/orders/:id/purchase-order (multipart `poFile`), which
 * saves `custom_fields.customer_purchase_order` on the Shopify order and logs
 * a Quote Portal Airtable event (`po-uploaded`).
 */
function POUploader({
  orderId: orderIdProp = null,
  showCard = true,
  showTitle = true,
  showDescription = true,
  title = 'Upload Purchase Order',
  className = '',
  message = 'Upload your purchase order here. We’ll attach it to this order.',
  onUploaded,
}) {
  const [orderId, setOrderId] = useState(orderIdProp || getOrderIdFromHash());
  const [file, setFile] = useState(null);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const [isGovPo, setIsGovPo] = useState(false);
  const [existingPoUrl, setExistingPoUrl] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);

  useEffect(() => {
    if (orderIdProp) {
      setOrderId(orderIdProp);
      return undefined;
    }

    const syncFromHash = () => setOrderId(getOrderIdFromHash());
    syncFromHash();
    window.addEventListener('hashchange', syncFromHash);
    return () => window.removeEventListener('hashchange', syncFromHash);
  }, [orderIdProp]);

  useEffect(() => {
    let cancelled = false;

    const loadExisting = async () => {
      if (!orderId) {
        setIsGovPo(false);
        setExistingPoUrl(null);
        setLoadingStatus(false);
        return;
      }

      setLoadingStatus(true);
      try {
        const { token } = getToken();
        if (!token) {
          setLoadingStatus(false);
          return;
        }
        const data = await fetchOrderById(orderId, token);
        const order = data?.order;
        if (!cancelled) {
          setIsGovPo(isGovernmentPoOrder(order));
          setExistingPoUrl(hasPurchaseOrderFile(order)
            ? (order.governmentPoDocument)
            : null);
        }
      } catch (err) {
        console.error('Failed to load existing purchase order:', err);
      } finally {
        if (!cancelled) setLoadingStatus(false);
      }
    };

    loadExisting();
    return () => {
      cancelled = true;
    };
  }, [orderId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!file) {
      setError('Please select a file to upload.');
      return;
    }

    const { token } = getToken();
    if (!token) {
      setError('No authentication token found. Please use the secure link from your email.');
      return;
    }

    if (!orderId) {
      setError('Open an order to upload a purchase order.');
      return;
    }

    try {
      setStatus('uploading');
      const data = await poUploadOrder(orderId, '', token, file);
      const url = data?.poUrl || data?.order?.governmentPoDocument || null;
      if (url) setExistingPoUrl(url);
      setStatus('success');

      pushDataLayerEvent('order_portal_po_uploaded', {
        order_id: orderId,
        order_name: data?.order?.name,
      });
      trackKlaviyoEvent('Order Portal PO Uploaded', {
        OrderId: orderId,
        OrderName: data?.order?.name,
        Source: 'po_uploader',
      });
      onUploaded?.(data);
    } catch (err) {
      console.error('Purchase order upload failed:', err);
      setError(err.message || 'Failed to upload purchase order');
      setStatus('error');
    }
  };

  if (!orderId || loadingStatus) return null;
  if (!isGovPo) return null;
  if (existingPoUrl && status !== 'success') return null;

  const PoForm = (
    <form onSubmit={handleSubmit}>
      <FileUploader
        accept=".pdf,.png,.jpg,.jpeg"
        maxSizeMB={15}
        onFileSelect={setFile}
      />

      {error && <p className="po-uploader-error">{error}</p>}

      <div className="quote-update-line quote-update-button-wrapper">
        <button
          type="submit"
          className="button button--primary button--full"
          disabled={status === 'uploading'}
        >
          {status === 'uploading' ? 'Uploading…' : 'Upload Purchase Order'}
        </button>
      </div>

      {status === 'success' && !error && (
        <p className="po-uploader-success">
          Your purchase order has been uploaded successfully.
          {existingPoUrl ? (
            <>
              {' '}
              <a href={existingPoUrl} target="_blank" rel="noopener noreferrer">
                View document
              </a>
            </>
          ) : null}
        </p>
      )}
    </form>
  );

  if (showCard) {
    return (
      <div className={`card po-uploader ${className}`.trim()} title={title}>
        <div className="card__section">
          {showTitle && title && (
            <h3 className="blog-sidebar__block-title heading h3 po-uploader-title">
              {title}
            </h3>
          )}
          {showDescription && message && (
            <p className="po-uploader-description">{message}</p>
          )}
          {PoForm}
        </div>
      </div>
    );
  }

  return (
    <div className={`po-uploader-no-text ${className}`.trim()} title={title}>
      {showDescription && message && (
        <p className="po-uploader-description">{message}</p>
      )}
      {PoForm}
    </div>
  );
}

export default POUploader;
